import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";

function VioletSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
        checked ? "bg-violet-600" : "bg-slate-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform translate-y-1 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", disabled, required, autoFocus, helper }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400
                   focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                   disabled:opacity-60 disabled:cursor-not-allowed"
      />
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

const BLANK_EF6 = {
  toolPath: "",
  assemblyPath: "",
  projectDirectory: "",
  migrationsConfigurationType: "",
  connectionStringName: "",
};

const BLANK = {
  name: "",
  serverName: "",
  apiBaseUrl: "",
  apiKey: "",
  gitPath: "",
  archiveToolPath: "",
  dotnetPath: "dotnet",
  npmPath: "npm",
  yarnPath: "yarn",
  ef6: { ...BLANK_EF6 },
  isActive: true,
};

function snapshotFrom(m) {
  return {
    name: m.name ?? "",
    serverName: m.serverName ?? "",
    apiBaseUrl: m.apiBaseUrl ?? "",
    apiKey: "",
    gitPath: m.gitPath ?? "",
    archiveToolPath: m.archiveToolPath ?? "",
    dotnetPath: m.dotnetPath ?? "dotnet",
    npmPath: m.npmPath ?? "npm",
    yarnPath: m.yarnPath ?? "yarn",
    ef6: {
      toolPath: m.ef6?.toolPath ?? "",
      assemblyPath: m.ef6?.assemblyPath ?? "",
      projectDirectory: m.ef6?.projectDirectory ?? "",
      migrationsConfigurationType: m.ef6?.migrationsConfigurationType ?? "",
      connectionStringName: m.ef6?.connectionStringName ?? "",
    },
    isActive: !!m.isActive,
  };
}

const TOP_KEYS = ["name", "serverName", "apiBaseUrl", "apiKey", "gitPath", "archiveToolPath", "dotnetPath", "npmPath", "yarnPath", "isActive"];
const EF6_KEYS = ["toolPath", "assemblyPath", "projectDirectory", "migrationsConfigurationType", "connectionStringName"];

function snapshotsEqual(a, b) {
  if (!a || !b) return a === b;
  if (!TOP_KEYS.every((k) => a[k] === b[k])) return false;
  return EF6_KEYS.every((k) => a.ef6?.[k] === b.ef6?.[k]);
}

function initials(name) {
  const base = (name || "?").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export default function MaquinasList() {
  const [maquinas, setMaquinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/machines`)
      .then((res) => setMaquinas(res.data || []))
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar máquinas";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(
    () => maquinas.find((m) => m.id === selectedId) || null,
    [maquinas, selectedId]
  );

  const isDirty = useMemo(
    () => form != null && original != null && !snapshotsEqual(form, original),
    [form, original]
  );

  const applyMachine = (m) => {
    const snap = snapshotFrom(m);
    setCreating(false);
    setSelectedId(m.id);
    setForm(snap);
    setOriginal(snap);
  };

  const enterCreateMode = () => {
    setCreating(true);
    setSelectedId(null);
    setForm({ ...BLANK });
    setOriginal({ ...BLANK });
  };

  const confirmDiscardOrSave = async () => {
    return AlertComponent(
      "Alterações não salvas",
      creating
        ? "As informações da nova máquina serão perdidas. Deseja salvar ou descartar as alterações?"
        : "As informações desta máquina foram alteradas. Deseja salvar ou descartar as alterações?",
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Salvar",
        cancelButtonText: "Descartar",
        confirmButtonColor: "#7c3aed",
        cancelButtonColor: "#64748b",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false,
        reverseButtons: true,
      },
      null,
      false
    );
  };

  const validate = (snap, isCreate) => {
    if (!snap.name.trim()) return "Informe o nome da máquina.";
    if (!snap.serverName.trim()) return "Informe o server name.";
    if (!snap.apiBaseUrl.trim()) return "Informe a URL base da API.";
    if (isCreate && !snap.apiKey.trim()) return "Informe a API key para a nova máquina.";
    return null;
  };

  const save = async (machine, snap, isCreate) => {
    const erro = validate(snap, isCreate);
    if (erro) {
      AlertComponent("Atenção", erro, { icon: "warning" });
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        name: snap.name,
        serverName: snap.serverName,
        apiBaseUrl: snap.apiBaseUrl,
        gitPath: snap.gitPath,
        archiveToolPath: snap.archiveToolPath,
        dotnetPath: snap.dotnetPath,
        npmPath: snap.npmPath,
        yarnPath: snap.yarnPath,
        ef6: { ...snap.ef6 },
        isActive: snap.isActive,
      };
      if (snap.apiKey && snap.apiKey.trim()) payload.apiKey = snap.apiKey;

      if (isCreate) {
        const res = await axios.post(`${process.env.REACT_APP_API}/admin/machines`, payload);
        const created = res.data || {};
        const enriched = {
          name: snap.name,
          serverName: snap.serverName,
          apiBaseUrl: snap.apiBaseUrl,
          gitPath: snap.gitPath,
          archiveToolPath: snap.archiveToolPath,
          dotnetPath: snap.dotnetPath,
          npmPath: snap.npmPath,
          yarnPath: snap.yarnPath,
          ef6: { ...snap.ef6 },
          isActive: snap.isActive,
          ...created,
        };
        setMaquinas((prev) => [enriched, ...prev]);
        AlertComponent("Sucesso", "Máquina criada com sucesso", { icon: "success" });
        return enriched;
      }

      await axios.put(`${process.env.REACT_APP_API}/admin/machines/${machine.id}`, payload);
      const updated = {
        ...machine,
        name: snap.name,
        serverName: snap.serverName,
        apiBaseUrl: snap.apiBaseUrl,
        gitPath: snap.gitPath,
        archiveToolPath: snap.archiveToolPath,
        dotnetPath: snap.dotnetPath,
        npmPath: snap.npmPath,
        yarnPath: snap.yarnPath,
        ef6: { ...snap.ef6 },
        isActive: snap.isActive,
      };
      setMaquinas((prev) => prev.map((m) => (m.id === machine.id ? updated : m)));
      setOriginal({ ...snap, apiKey: "" });
      setForm((prev) => (prev ? { ...prev, apiKey: "" } : prev));
      AlertComponent("Sucesso", "Alterações salvas com sucesso", { icon: "success" });
      return updated;
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao salvar";
      AlertComponent("Erro", mensagem, { icon: "error" });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = async (m) => {
    if (!creating && m.id === selectedId) return;

    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await save(selected, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }
    applyMachine(m);
  };

  const handleNew = async () => {
    if (creating && !isDirty) return;
    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await save(selected, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }
    enterCreateMode();
  };

  const handleSave = async () => {
    if (!form || !isDirty) return;
    if (creating) {
      const created = await save(null, form, true);
      if (created) applyMachine(created);
    } else if (selected) {
      await save(selected, form, false);
    }
  };

  const handleSyncTargets = async () => {
    if (!selected || syncing) return;
    setSyncing(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/admin/machines/${selected.id}/sync-targets`);
      AlertComponent("Sucesso", "Targets sincronizados com sucesso", { icon: "success" });
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao sincronizar targets";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const update = (key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateEf6 = (key, value) => {
    setForm((prev) => (prev ? { ...prev, ef6: { ...prev.ef6, [key]: value } } : prev));
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />
        <div className="relative">
          <p className="text-purple-300 text-sm font-medium mb-1">Administração</p>
          <h1 className="text-2xl sm:text-3xl font-bold">Máquinas</h1>
          <p className="text-purple-300 text-sm mt-1">Gerencie os servidores orquestrados para deploy.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
        {/* Esquerda: lista */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-violet-500" />
              <h2 className="text-base font-bold text-slate-800">Máquinas</h2>
              {!loading && (
                <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
                  {maquinas.length}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleNew}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Nova
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : maquinas.length === 0 && !creating ? (
            <div className="text-center py-16 px-6">
              <p className="text-slate-500 font-medium">Nenhuma máquina cadastrada</p>
              <p className="text-slate-400 text-sm mt-1">Clique em <strong className="text-violet-600">Nova</strong> para começar.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 overflow-y-auto max-h-[calc(100vh-280px)]">
              {creating && (
                <li>
                  <div className="w-full text-left flex items-center gap-3 px-5 py-3.5 bg-violet-50 ring-1 ring-inset ring-violet-200">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 ring-2 ring-violet-300 ring-offset-2"
                      style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-violet-900 truncate">
                          {(form?.name || "").trim() || "Nova máquina"}
                        </p>
                        {isDirty && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                            Não salvo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-violet-600 truncate mt-0.5">
                        {(form?.serverName || "").trim() || "Preencha os dados à direita"}
                      </p>
                    </div>
                  </div>
                </li>
              )}
              {maquinas.map((m) => {
                const isSelected = m.id === selectedId;
                const isDirtyHere = isSelected && isDirty;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(m)}
                      className={`w-full text-left flex items-center gap-3 px-5 py-3.5 transition-colors ${
                        isSelected
                          ? "bg-violet-50 ring-1 ring-inset ring-violet-200"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                          isSelected ? "ring-2 ring-violet-300 ring-offset-2" : ""
                        }`}
                        style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                      >
                        {initials(m.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold truncate ${isSelected ? "text-violet-900" : "text-slate-800"}`}>
                            {m.name}
                          </p>
                          {!m.isActive && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                              Inativa
                            </span>
                          )}
                          {typeof m.targetCount === "number" && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200">
                              {m.targetCount} target{m.targetCount !== 1 ? "s" : ""}
                            </span>
                          )}
                          {isDirtyHere && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                              Não salvo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5 font-mono">{m.serverName}</p>
                      </div>
                      <svg className={`w-4 h-4 shrink-0 ${isSelected ? "text-violet-500" : "text-slate-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Direita: form */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
              <h2 className="text-base font-bold text-slate-800 shrink-0">
                {creating ? "Nova máquina" : "Detalhes"}
              </h2>
              {!creating && selected && (
                <span className="ml-1 text-xs text-slate-500 truncate">· {selected.name}</span>
              )}
            </div>
            {isDirty && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-200 shrink-0">
                Não salvo
              </span>
            )}
          </div>

          {!form ? (
            <div className="flex-1 text-center py-20 px-6">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
              </div>
              <p className="text-slate-500 font-medium mb-1">Selecione uma máquina</p>
              <p className="text-slate-400 text-sm">Ou clique em <strong className="text-violet-600">Nova</strong> para cadastrar uma nova.</p>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto flex-1 p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label="Nome"
                    value={form.name}
                    onChange={(v) => update("name", v)}
                    placeholder="Máquina de produção"
                    disabled={saving}
                    required
                  />
                  <TextField
                    label="Server name"
                    value={form.serverName}
                    onChange={(v) => update("serverName", v)}
                    placeholder="EC2AMAZ-ABC123"
                    disabled={saving}
                    required
                  />
                </div>

                <TextField
                  label="URL base da API"
                  value={form.apiBaseUrl}
                  onChange={(v) => update("apiBaseUrl", v)}
                  placeholder="https://deploy01.interno.com"
                  type="url"
                  disabled={saving}
                  required
                  helper="Endereço onde o agente dessa máquina está escutando."
                />

                <TextField
                  label="API Key"
                  value={form.apiKey}
                  onChange={(v) => update("apiKey", v)}
                  placeholder={creating ? "sk_live_…" : "Deixe em branco para manter a atual"}
                  type="password"
                  disabled={saving}
                  required={creating}
                  helper={creating ? "Chave usada para autenticar o orquestrador." : "Preencha apenas se quiser substituir a API key existente."}
                />

                <div className="pt-3 border-t border-slate-100">
                  <h3 className="text-sm font-bold text-slate-800 mb-3">Ferramentas locais</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextField
                      label="Caminho do Git"
                      value={form.gitPath}
                      onChange={(v) => update("gitPath", v)}
                      placeholder="C:\\Program Files\\Git\\cmd\\git.exe"
                      disabled={saving}
                    />
                    <TextField
                      label="Archive tool (7z, etc.)"
                      value={form.archiveToolPath}
                      onChange={(v) => update("archiveToolPath", v)}
                      placeholder="C:\\Program Files\\7-Zip\\7z.exe"
                      disabled={saving}
                    />
                    <div className="sm:col-span-2">
                      <TextField
                        label="Caminho do dotnet"
                        value={form.dotnetPath}
                        onChange={(v) => update("dotnetPath", v)}
                        placeholder="C:\\Program Files\\dotnet\\dotnet.exe"
                        disabled={saving}
                        helper="Usado por build dotnet publish e migrations EF Core."
                      />
                    </div>
                    <TextField
                      label="Caminho do npm"
                      value={form.npmPath}
                      onChange={(v) => update("npmPath", v)}
                      placeholder="C:\\Program Files\\nodejs\\npm.cmd"
                      disabled={saving}
                    />
                    <TextField
                      label="Caminho do Yarn"
                      value={form.yarnPath}
                      onChange={(v) => update("yarnPath", v)}
                      placeholder="yarn"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <h3 className="text-sm font-bold text-slate-800 mb-0.5">EF6 Migrations</h3>
                  <p className="text-xs text-slate-500 mb-3">Caminhos usados pelo orquestrador para rodar migrations do Entity Framework 6 nesta máquina.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextField
                      label="Tool path"
                      value={form.ef6.toolPath}
                      onChange={(v) => updateEf6("toolPath", v)}
                      placeholder="packages\\EntityFramework.6.4.4\\tools\\net45\\any\\ef6.exe"
                      disabled={saving}
                    />
                    <TextField
                      label="Assembly path"
                      value={form.ef6.assemblyPath}
                      onChange={(v) => updateEf6("assemblyPath", v)}
                      placeholder="Project\\bin\\Debug\\Project.dll"
                      disabled={saving}
                    />
                    <TextField
                      label="Project directory"
                      value={form.ef6.projectDirectory}
                      onChange={(v) => updateEf6("projectDirectory", v)}
                      placeholder="MyProject.Entity"
                      disabled={saving}
                    />
                    <TextField
                      label="Connection string name"
                      value={form.ef6.connectionStringName}
                      onChange={(v) => updateEf6("connectionStringName", v)}
                      placeholder="Context"
                      disabled={saving}
                    />
                    <div className="sm:col-span-2">
                      <TextField
                        label="Migrations configuration type"
                        value={form.ef6.migrationsConfigurationType}
                        onChange={(v) => updateEf6("migrationsConfigurationType", v)}
                        placeholder="MyProject.Entity.Migrations.Configuration"
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>

                <label className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition cursor-pointer ${
                  form.isActive ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
                }`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Máquina ativa</p>
                    <p className="text-xs text-slate-500 mt-0.5">Desative para excluir esta máquina de novos deploys.</p>
                  </div>
                  <VioletSwitch checked={form.isActive} onChange={(v) => update("isActive", v)} disabled={saving} />
                </label>

                {!creating && selected && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Ações</p>
                    {selected.lastSyncedAt && (
                      <p className="text-[11px] text-slate-500 mb-2">
                        Último sync: {new Date(selected.lastSyncedAt).toLocaleString("pt-BR", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
                        })}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleSyncTargets}
                      disabled={syncing || saving}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {syncing ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Sincronizando...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Sincronizar targets
                        </>
                      )}
                    </button>
                    <p className="text-[11px] text-slate-500 mt-1.5">Varre esta máquina e atualiza a listagem de deploy targets.</p>
                  </div>
                )}
              </div>

              {/* Save bar */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500 min-w-0 truncate">
                  {isDirty ? "Há alterações pendentes de salvamento." : "Nenhuma alteração pendente."}
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isDirty || saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                  style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
